import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Modal,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import PackageAPI from 'api/faber/PackageAPI'
import UsaAPI from 'api/faber/UsaAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import NormalText from 'common/Typography/NormalText'
import { format } from 'date-fns'
import OrbitSession from 'hooks/useCookies'
import {
  GetActiveUserPlansDetailsType,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType,
  UserPlanWithPrice
} from 'types/api/faber/oneTimePack.type'

import ReassignPackageDialog from '../dialogs/ReassignPackageDialog'

interface Props {
  userPacksUserPlans: UserPlanWithPrice[]
  setOtpackDataRemaining: (value: number) => void
  isExternal: boolean
  userId: string
  pageNumber: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  pageCount: number
  isFetchingOTPackUserPlans: boolean
}

const userPacksColumns = (
  handleReassignPackage: (row: UserPlanWithPrice) => void,
  handleResetPackageActive: (row: UserPlanWithPrice) => void,
  handleResetPackageInactive: (row: UserPlanWithPrice) => void,
  handleTerminatePackageActive: (row: UserPlanWithPrice) => void,
  handleTerminatePackageInactive: (row: UserPlanWithPrice) => void,
  handleShowSelectedPackInfo: (row: UserPlanWithPrice) => void,
  handleViewUser: (row: UserPlanWithPrice) => void
) => [
  {
    field: 'packageName',
    headerName: 'Packs',
    sortable: false,
    flex: 1.5,
    minWidth: 150,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <Grid item xs={2}>
        <Stack
          flexDirection={'column'}
          sx={{ alignItems: 'start', padding: 0, margin: 0 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              padding: 0,
              margin: 0
            }}
          >
            <NormalText sx={{ padding: 0, margin: 0 }}>
              {params.row.packageName}
            </NormalText>
            <Tooltip title={''}>
              <InfoIcon
                onClick={() => handleShowSelectedPackInfo(params.row)}
                style={{ color: 'lightgrey', fontSize: '18' }}
              />
            </Tooltip>
          </Box>
          {params.row.allocatedFrom && (
            <div onClick={() => handleViewUser(params.row)}>
              <Typography
                sx={{
                  fontSize: '12px',
                  padding: 0,
                  margin: 0,
                  marginBottom: 1,
                  color: 'blue',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                from: {params.row.allocatedFrom ?? 'N/A'}
              </Typography>
            </div>
          )}
        </Stack>
      </Grid>
    )
  },
  {
    field: 'iccid',
    headerName: 'ICCID',
    sortable: false,
    flex: 1.5,
    minWidth: 100,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <Grid item xs={2}>
        <NormalText theme='muted'>{params.row.iccid}</NormalText>
      </Grid>
    )
  },
  {
    field: 'dataUsageRemainingInBytes',
    headerName: 'Data Usage',
    sortable: false,
    flex: 1.5, // Increased from 0.8 to make column wider
    minWidth: 200, // Increased from 120 to ensure minimum width
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <Grid style={{ width: '100%', padding: '8px' }}> {/* Added padding for better spacing */}
        <LinearProgress
          variant="determinate"
          value={
            ((params.row.packageDataAllowed -
              params.row.dataUsageRemainingInBytes / 1024 * 1024 * 1024) /
              params.row.packageDataAllowed) *
            100
          }
          sx={{
            height: 12, // Increased height from default (usually 4px)
            borderRadius: 6,
            width: '100%',
            marginBottom: 1
          }}
        />
        <div style={{ fontSize: '14px', fontWeight: 500 }}>
          {parseFloat(
            (
              params.row.packageDataAllowed -
              params.row.dataUsageRemainingInBytes / 1073741824
            ).toFixed(2)
          )}
          /{params.row.packageDataAllowed} GB
        </div>
      </Grid>
    )
  },
  {
    field: 'createdAt',
    headerName: 'Purchased',
    sortable: false,
    flex: 0.8,
    minWidth: 120,
    valueFormatter: ({ value }: { value: string }) =>
      value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    flex: 0.6,
    minWidth: 100,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <Grid item xs={2}>
        <NormalText theme='muted'>{params.row.planStatus}</NormalText>
      </Grid>
    )
  },
  {
    field: 'packageValidityInDays',
    headerName: 'Validity',
    sortable: false,
    flex: 1,
    minWidth: 120,
    renderCell: (params: { row: UserPlanWithPrice }) => (
      <Grid item xs={1}>
        <NormalText>{params.row.packageValidityInDays} days</NormalText>
      </Grid>
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    minWidth: 80,
    renderCell: (params: { row: UserPlanWithPrice }) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

      const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
      }
      const handleClose = () => {
        setAnchorEl(null)
      }

      return (
        <>
          <IconButton onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: { minWidth: 150 }
            }}
          >
            <MenuItem
              onClick={() => {
                handleReassignPackage(params.row)
                handleClose()
              }}
            >
              Reassign Package
            </MenuItem>
            <MenuItem
              onClick={() => {
                params.row.planStatus === 'INACTIVE'
                  ? handleResetPackageInactive(params.row)
                  : handleResetPackageActive(params.row)

                handleClose()
              }}
            >
              Reset Package
            </MenuItem>
            <MenuItem
              onClick={() => {
                params.row.planStatus === 'INACTIVE'
                  ? handleTerminatePackageInactive(params.row)
                  : handleTerminatePackageActive(params.row)

                handleClose()
              }}
            >
              Terminate Package
            </MenuItem>
          </Menu>
        </>
      )
    }
  }
]

export default function CustomerPlansCard({
  userPacksUserPlans,
  setOtpackDataRemaining,
  isExternal,
  userId,
  pageNumber,
  setPage,
  pageCount,
  isFetchingOTPackUserPlans
}: Props) {
  const faberToken: string = OrbitSession.get('faberToken')
  const navigate = useNavigate()

  const [selectedItem, setSelectedItem] = useState<
    | GetActiveUserPlansDetailsType
    | GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
    | undefined
  >(undefined)
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false)
  const [showMoreResetPackageInfoModal, setShowMoreResetPackageInfoModal] =
    useState(false)
  const [
    showMoreTerminatePackageInfoModal,
    setShowMoreTerminatePackageInfoModal
  ] = useState(false)
  const [showReassignDialog, setShowReassignDialog] = useState(false)

  const { data: packageDetails } = useQuery(
    ['getPackageDetails', selectedItem?.appliedPlanId ?? ''],
    async () =>
      isExternal
        ? PackageAPI.getExternalPackageDetails(
            selectedItem?.appliedPlanId ?? '',
            faberToken
          )
        : PackageAPI.getPackageDetails(
            selectedItem?.appliedPlanId ?? '',
            faberToken
          ),
    { enabled: !!selectedItem }
  )
  const packageData = packageDetails?.data.data

  const [selectedPackTotalData, setSelectedPackTotalData] = useState(0)
  const [selectedPackRemainingData, setSelectedPackRemainingData] = useState(0)

  // If more than 100MB, they are not allowed to reset the pack
  // const overResetLimit: boolean = (selectedPackUsedData / (1024 * 1024 * 1024) > 0.1) ? true : false
  let overResetLimit: boolean
  if (
    selectedPackTotalData - selectedPackRemainingData / (1024 * 1024 * 1024) >
    0.1
  ) {
    overResetLimit = true
  } else {
    overResetLimit = false
  }

  const resetPackage = () => {
    if (packageData === undefined || packageData.packageId === undefined) {
      toast.error(
        'Unable to replace package as packageId is undefined. If the values are empty, give it a moment to let it load'
      )
      return
    }

    // Depending on whether this is a external user page, use the right replace package api
    (isExternal
      ? PackageAPI.externalReplacePackage(packageData.packageId, faberToken)
      : PackageAPI.replacePackage(packageData.packageId, faberToken)
    )
      .then(() => {
        toast.success(
          'Successfully replaced package, please refresh the page to see the new updated PackageID'
        )
        return
      })
      .catch(() => {
        toast.error(
          'Failed and did not replace the package, please contact the engineers'
        )
        return
      })

    return
  }

  const terminatePackage = () => {
    if (packageData === undefined || packageData.packageId === undefined) {
      toast.error(
        'Unable to terminate package as packageId is undefined. If the values are empty, give it a moment to let it load'
      )
      return
    }

    // Depending on whether this is a external user page, use the right replace package api
    (isExternal
      ? PackageAPI.externalTerminatePackage(packageData.packageId, faberToken)
      : PackageAPI.terminatePackage(packageData.packageId, faberToken)
    )
      .then(() => {
        toast.success(
          `Successfully terminated package, please refresh the page to verify the package ID ${packageData.packageId} is terminated`
        )
        return
      })
      .catch(() => {
        toast.error(
          'Failed and was not able to terminate the package, please contact the engineers'
        )
        return
      })

    return
  }

  const getUsers = async (user_id: string) => {
    if (user_id.includes('|')) {
      // Global users
      const response = await UsaAPI.getAllGlobalUsers({
        searchCriteria: 'User ID',
        searchValue: user_id,
        faberToken: faberToken
      })
      if (response.status === 200) {
        // Mapping present in FaberGlobalUserSearch.tsx
        return response.data.data.users.map(
          ({ name, family_name, given_name, user_id, user_metadata }) => ({
            name,
            family_name,
            given_name,
            user_id,
            user_metadata,
            login_method: user_id
          })
        )
      }
    } else {
      const response = await OneTimePackApi.getAllFaberUsers({
        searchCriteria: 'Customer ID',
        searchValue: user_id,
        faberToken: faberToken,
        limit: 10,
        planStatus: 'active',
        offset: 0
      })
      if (response.status === 200) {
        // Mapping present in RevampedFaberCustomerDashboard.tsx
        return response.data.body.data.map(
          ({
            _id,
            user_id,
            user_metadata,
            email,
            contactNo,
            address,
            createdAt,
            firstName,
            lastName,
            role,
            showPlanPage,
            showTutorial,
            sin,
            status,
            updatedAt,
            subscription,
            otpack
          }) => ({
            _id,
            user_id,
            user_metadata,
            contactNo,
            firstName,
            email,
            msisdn: contactNo.number,
            address,
            createdAt,
            lastName,
            role,
            showPlanPage,
            showTutorial,
            sin,
            status,
            updatedAt,
            subscription,
            otpack
          })
        )
      }
    }
    return []
  }

  const handleViewUser = async (item) => {
    const userId = item?.allocatedFromID
    const userInfo = await getUsers(userId)
    if (userInfo.length == 1) {
      navigate(`/hq/manage-global-cx/view-profile/${userId}`, {
        state: userInfo[0]
      })
    } else {
      toast.error('Error fetching promocode user profile', {
        toastId: 'error-get-promocode-user-profile'
      })
    }
  }

  const handleReassignPackage = (item) => {
    setSelectedItem(item)
    setShowReassignDialog(true)
  }

  const handleResetPackageActive = (item) => {
    setSelectedItem(item)
    setShowMoreResetPackageInfoModal(true)
    setSelectedPackTotalData(item.packageDataAllowed)
    setSelectedPackRemainingData(item.dataUsageRemainingInBytes)
  }

  const handleTerminatePackageActive = (item) => {
    setSelectedItem(item)
    setShowMoreTerminatePackageInfoModal(true)
    setSelectedPackTotalData(item.packageDataAllowed)
    setSelectedPackRemainingData(item.dataUsageRemainingInBytes)
  }

  const handleResetPackageInactive = (item) => {
    setSelectedItem(item)
    setShowMoreResetPackageInfoModal(true)
    setSelectedPackRemainingData(item.packageDataAllowed * 1024 * 1024 * 1024) // remaining data to be in bytes
    setSelectedPackTotalData(item.packageDataAllowed)
  }

  const handleTerminatePackageInactive = (item) => {
    setSelectedItem(item)
    setShowMoreTerminatePackageInfoModal(true)
    setSelectedPackRemainingData(item.packageDataAllowed * 1024 * 1024 * 1024) // remaining data to be in bytes
    setSelectedPackTotalData(item.packageDataAllowed)
  }

  const handleShowSelectedPackInfo = (item) => {
    setSelectedItem(item)
    setShowMoreInfoModal(true)
  }

  useEffect(() => {
    if (userPacksUserPlans) {
      const totalDataLeft = userPacksUserPlans.reduce(
        (accumulator, item) =>
          accumulator + item.dataUsageRemainingInBytes / 1073741824,
        0
      )
      setOtpackDataRemaining(parseFloat(totalDataLeft.toFixed(2)))
    }
  }, [userPacksUserPlans])

  return (
    <>
      <Stack sx={{ paddingBottom: '21px' }}>
        {/* The information pop up */}
        <Modal
          open={showMoreInfoModal}
          onClose={() => setShowMoreInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom>
              Package Details
            </Typography>
            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>
            <NormalText>
              Data Remaining:{' '}
              {packageData?.dataUsageRemainingInBytes
                ? packageData.dataUsageRemainingInBytes / (1024 * 1024 * 1024) +
                  ' GB'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                      new Date(packageData.dateCreated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                      new Date(packageData.dateExpiry),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                      new Date(packageData.dateActivated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                      new Date(packageData.dateTerminated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
          </Box>
        </Modal>

        {/* The reset information pop up */}
        <Modal
          open={showMoreResetPackageInfoModal}
          onClose={() => setShowMoreResetPackageInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom>
              Do you really want to reset the package?
            </Typography>
            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>

            <NormalText sx={{ color: overResetLimit ? 'red' : 'green' }}>
              {/* Data Remaining:{' '} */}
              {/* {packageData?.dataUsageRemainingInBytes
                ? packageData.dataUsageRemainingInBytes / (1024 * 1024 * 1024) +
                  ' GB'
                : 'N/A'} */}
              {/* Data Used:{' '} */}
              {/* {packageData?.dataUsageRemainingInBytes
                ? 
                (100 - packageData?.dataUsageRemainingInBytes / (1024 * 1024 * 1024) + ' GB')
                : 'N/A'} */}
              Data Used:{' '}
              {
                // parseFloat((selectedPackUsedData / (1024 * 1024 * 1024)).toFixed(2))
                (
                  selectedPackTotalData -
                  selectedPackRemainingData / (1024 * 1024 * 1024)
                ).toFixed(2) + ' GB'
              }
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                      new Date(packageData.dateCreated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                      new Date(packageData.dateExpiry),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                      new Date(packageData.dateActivated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                      new Date(packageData.dateTerminated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>

            <Button
              variant='contained'
              disabled={overResetLimit} // Only allow the button if it is not over the limit
              onClick={() => {
                setShowMoreResetPackageInfoModal(false)
                resetPackage()
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setShowMoreResetPackageInfoModal(false)
              }}
            >
              No
            </Button>
          </Box>
        </Modal>

        {/* The terminate information pop up */}
        <Modal
          open={showMoreTerminatePackageInfoModal}
          onClose={() => setShowMoreTerminatePackageInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom color='red'>
              Do you really want to terminate the package?
            </Typography>

            <Typography variant='h6' gutterBottom color='red'>
              This will terminate the pack immediately and cannot be undone.
              Please proceed with caution
            </Typography>

            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>

            {/* No color formatting needed because this is not replacing a package */}
            <NormalText>
              Data Used:{' '}
              {(
                selectedPackTotalData -
                selectedPackRemainingData / (1024 * 1024 * 1024)
              ).toFixed(2) + ' GB'}
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                      new Date(packageData.dateCreated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                      new Date(packageData.dateExpiry),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                      new Date(packageData.dateActivated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                      new Date(packageData.dateTerminated),
                      'dd MMM yyyy HH:mm'
                    ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>

            <Button
              sx={{
                color: 'white',
                background: 'red'
              }}
              variant='contained'
              onClick={() => {
                setShowMoreTerminatePackageInfoModal(false)
                terminatePackage()
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setShowMoreTerminatePackageInfoModal(false)
              }}
            >
              No
            </Button>
          </Box>
        </Modal>

        <ReassignPackageDialog
          open={showReassignDialog}
          onClose={() => setShowReassignDialog(false)}
          userId={userId}
          packageId={selectedItem?.appliedPlanId ?? ''}
          faberToken={faberToken}
        />

        <Stack>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <NormalText isBold sx={{ fontSize: '21px' }}>
                Active/Inactive Packs
              </NormalText>
            </Grid>
          </Grid>
          <NormalText theme='muted'>
            The current status of the customer&apos;s active/unused pack(s).
          </NormalText>

          <MUIDataGrid
            columns={userPacksColumns(
              (row) => handleReassignPackage(row),
              (row) => handleResetPackageActive(row),
              (row) => handleResetPackageInactive(row),
              (row) => handleTerminatePackageActive(row),
              (row) => handleTerminatePackageInactive(row),
              (row) => handleShowSelectedPackInfo(row),
              (row) => handleViewUser(row)
            )}
            rows={userPacksUserPlans}
            getRowId={(row: UserPlanWithPrice) => row.appliedPlanId}
            pageCount={pageCount}
            pageNumber={pageNumber}
            onPageChange={(newPageNumber) => setPage(newPageNumber - 1)}
            disableColumnMenu
            loading={isFetchingOTPackUserPlans}
          />
        </Stack>
      </Stack>
    </>
  )
}
const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  p: 4
}
